import { Stack } from '@fluentui/react';
import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useStore } from '../../stores/root';
import PermissionsService from '../../services/permissions';

import ControlTowerSummary from '../../components/dashboard/controlTowerSummary';
import DashboardSchedule from '../../components/dashboard/schedule';
import ActiveOrdersWidget from '../../components/dashboard/activeOrdersWidget';
import DelayedShipmentsWidget from '../../components/dashboard/delayedShipmentsWidget';
import FinancialReceivablesWidget from '../../components/dashboard/financialReceivablesWidget';
import AdminChatbotWidget from '../../components/uiframeworks/chatbot/AdminChatbotWidget';
import ErrorBoundary from '../../components/feedbacks/errorBoundary';

import styles from './style.module.scss';

interface IDashboardPageProps {}

const DashboardPage: React.FC<IDashboardPageProps> = observer((props: IDashboardPageProps) => {
    const store = useStore();
    const userRoles = store.user?.roles || [];
    const userPermissions = store.user?.permissions || [];

    // Role-based permission rules for Control Tower Dashboard:
    // 1. Financial summary cards (Pendapatan, Biaya, Profit): Superuser, Administrator, Finance
    const canSeeSummaryCards = PermissionsService.hasPermission(['superuser', 'administrator', 'admin', 'finance'], userRoles) ||
        PermissionsService.hasPermission(['incomes.invoices.read.all', 'incomes.payments.read.all'], userPermissions);

    // 2. Active Orders & Delayed Shipments sections: Finance and Administrator
    const canSeeActiveOrders = PermissionsService.hasPermission(['superuser', 'administrator', 'admin', 'finance'], userRoles);
    const canSeeDelayedShipments = PermissionsService.hasPermission(['superuser', 'administrator', 'admin', 'finance'], userRoles);

    // 3. Financial Receivables (Piutang dan Kewajiban) section: Finance and Administrator
    const canSeeReceivables = PermissionsService.hasPermission(['finance', 'superuser', 'administrator', 'admin'], userRoles);

    useEffect(() => {
        store.topbar.show('Dashboard', []);
    }, []);

    const hasLeftColumn = canSeeActiveOrders;
    const hasRightColumn = canSeeDelayedShipments || canSeeReceivables;

    return (
        <Stack className={styles.dashboard} tokens={{ childrenGap: 24 }}>
            <div className={styles.controlTowerContainer}>
                {/* 1. Control Tower Title & 4 Financial Metric Cards (Protected by RBAC) */}
                {canSeeSummaryCards && <ControlTowerSummary showCards={true} />}

                {/* 2. Activities Schedule */}
                <div style={{ marginBottom: 24 }}>
                    <ErrorBoundary>
                        <DashboardSchedule />
                    </ErrorBoundary>
                </div>

                {/* 3. Grid Row: Active Orders on Left, Delayed Shipments & Financial Receivables on Right */}
                {(hasLeftColumn || hasRightColumn) && (
                    <div className={`${styles.widgetsGrid} ${!hasLeftColumn || !hasRightColumn ? styles.singleColumn : ''}`}>
                        {hasLeftColumn && (
                            <div className={styles.columnLeft}>
                                {canSeeActiveOrders && <ActiveOrdersWidget />}
                            </div>
                        )}
                        {hasRightColumn && (
                            <div className={styles.columnRight}>
                                {canSeeDelayedShipments && <DelayedShipmentsWidget />}
                                {canSeeReceivables && <FinancialReceivablesWidget />}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <AdminChatbotWidget />
        </Stack>
    );
});

export default DashboardPage;
